import { FilterSelection } from "@amzn/stencil-react-components/filtering";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { config } from "config";
import { useCallback, useMemo } from "react";

import { SortConfig } from "./sortingConfig";

interface SortState {
  bestMatch: { value: FilterSelection | undefined; setter: (value: FilterSelection) => void };
  price: { value: FilterSelection | undefined; setter: (value: FilterSelection) => void };
  alphabetic: { value: FilterSelection | undefined; setter: (value: FilterSelection) => void };
  ratings: { value: FilterSelection | undefined; setter: (value: FilterSelection) => void };
  views: { value: FilterSelection | undefined; setter: (value: FilterSelection) => void };
}

interface UseSortingLogicProps {
  bestMatchSort: FilterSelection | undefined;
  setBestMatchSort: (value: FilterSelection) => void;
  priceSort: FilterSelection | undefined;
  setPriceSort: (value: FilterSelection) => void;
  alphabeticSort: FilterSelection | undefined;
  setAlphabeticSort: (value: FilterSelection) => void;
  ratingsSort?: FilterSelection | undefined;
  setRatingsSort?: (value: FilterSelection) => void;
  viewsSort?: FilterSelection | undefined;
  setViewsSort?: (value: FilterSelection) => void;
  sortConfigs: SortConfig[];
  onClearSorting: () => void;
}

export const useSortingLogic = ({
  bestMatchSort,
  setBestMatchSort,
  priceSort,
  setPriceSort,
  alphabeticSort,
  setAlphabeticSort,
  ratingsSort,
  setRatingsSort,
  viewsSort,
  setViewsSort,
  sortConfigs,
  onClearSorting,
}: UseSortingLogicProps) => {
  // Map of sort values and setters
  const sortState: SortState = useMemo(
    () => ({
      bestMatch: { value: bestMatchSort, setter: setBestMatchSort },
      price: { value: priceSort, setter: setPriceSort },
      alphabetic: { value: alphabeticSort, setter: setAlphabeticSort },
      ratings: { value: ratingsSort, setter: setRatingsSort || (() => {}) },
      views: { value: viewsSort, setter: setViewsSort || (() => {}) },
    }),
    [
      bestMatchSort,
      setBestMatchSort,
      priceSort,
      setPriceSort,
      alphabeticSort,
      setAlphabeticSort,
      ratingsSort,
      setRatingsSort,
      viewsSort,
      setViewsSort,
    ]
  );

  // Generic UXM tracking function
  const trackUxmFilterListChange = useCallback((touchpoint: string, selection?: FilterSelection) => {
    const trackerId = config.analytics.uxmAppId;
    const filterListPayload = { selection: selection ?? "" };
    trackCustomEvent(filterListPayload, touchpoint, trackerId);
  }, []);

  // Generic filter change handler
  const createFilterChangeHandler = useCallback(
    (config: SortConfig) => (selection?: FilterSelection) => {
      trackUxmFilterListChange(config.touchpoint, selection);
      sortState[config.key as keyof typeof sortState].setter(selection);
    },
    [trackUxmFilterListChange, sortState]
  );

  // Handle filter changes from FilterBar
  const handleFilterChange = useCallback(
    (filterKey: string) => (filterValue?: FilterSelection) => {
      const config = sortConfigs.find((c) => c.key === filterKey);
      if (config) {
        createFilterChangeHandler(config)(filterValue);
      }
    },
    [sortConfigs, createFilterChangeHandler]
  );

  // Handle apply from FilterBar (detects reset operations)
  const handleApply = useCallback(
    (selections: Record<string, FilterSelection>) => {
      const hasAnySelection = Object.values(selections).some((selection) => selection && selection !== "");
      if (!hasAnySelection) {
        onClearSorting();
      }
    },
    [onClearSorting]
  );

  return {
    sortState,
    handleFilterChange,
    handleApply,
  };
};
